import React, {
  useEffect, useState, useContext, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  MarketingPreferencesDS,
  RadioButton
} from '@comicrelief/component-library';
import { snakeCase } from 'lodash';

import {
  Divider,
  SubmitContainer,
  SubmitButton,
  SoftHider,
  DescriptionWrapper,
  MarketingPreferencesWrapper,
  FundraisingPackWrapper,
  JustGivingFieldset,
  JustGivingPill,
  JustGivingWrapper,
  RadioButtonWrapper
} from './FundraiserSignup.style';

import {
  FIELDS,
  FUNDRAISING_GROUPS,
  PRINTED_FUNDRAISING_PACK,
  PACK_SEGMENTS,
  CAMPAIGN_STRING
} from './constants';

import mpTopCopy from './mpCopy';

import Fieldset from '../../components/Fieldset/Fieldset';
import TermsAndConditions from './components/TermsAndConditions';
// import PrizeDrawOptIn from './components/PrizeDrawOptIn';
import renderBody from '../../components/RichText/RichText';
import JustGivingWhiteLogo from './assets/JustGivingWhiteLogo.svg';
import FundraiserSignupContext from './context/FundraiserSignupContext';
import { MpValidationOptionsPropTypes } from './PropTypes';

const JustGivingMarketingSection = ({
  title,
  mpValidationOptions,
  showJGQuestion,
  justGivingPageForSchools,
  justGivingPageForWorkplace,
  justGivingPageForMyselfGroup,
  justGivingLabel,
  showFundraisingPackQuestion,
  fundraisingPackSegments,
  campaignCode,
  description = 'Tell us about your plans',
  justGivingDescription = 'Tell us more about your JustGiving page'
}) => {
  const {
    isSubmitted,
    register,
    watch,
    fundraisingGroup,
    setValue,
    isSubmitting,
    methods,
    getError
  } = useContext(FundraiserSignupContext);
  const firstName = watch(FIELDS.FIRST_NAME);
  const orgName = watch(FIELDS.ORG_NAME);
  const selectedFundraisingSegment = watch(FIELDS.SEGMENT);
  const isSchool = fundraisingGroup === FUNDRAISING_GROUPS.SCHOOL;
  const isWork = fundraisingGroup === FUNDRAISING_GROUPS.WORK;
  const isPublic = fundraisingGroup === FUNDRAISING_GROUPS.PUBLIC;
  const [mappedPackSegments, setMappedPackSegments] = useState(false);

  // Initially, just use the 'global' CMS option for this functionality:
  let showPackFiltered = showFundraisingPackQuestion;

  // On initial mount only...
  useEffect(() => {
    // ... check if we have specified any 'show FR pack option' segments in the CMS first:
    if (fundraisingPackSegments) {
      const segmentsToShowPackOption = [];

      // If so, for each group we want to show the FR pack option to, check it's valid and use the
      // 'internal' mapped name (for ease of comparison later), storing it in our state array:
      fundraisingPackSegments.forEach((thisGroup) => {
        const convertedName = snakeCase(thisGroup);
        if (PACK_SEGMENTS[convertedName]) {
          segmentsToShowPackOption.push(PACK_SEGMENTS[convertedName]);
        }
      });
      setMappedPackSegments(segmentsToShowPackOption);
    }
  }, [fundraisingPackSegments]);

  useEffect(() => {
    if (orgName || firstName) {
      const thisSuffix = `'s ${CAMPAIGN_STRING(campaignCode) || 'Page'}`;
      const thisName = orgName || firstName;
      // Strip out any chars not allowed by the page_title validation, and
      // trim the string to suit the backend.
      const thisNameFormatted = thisName.replace(/[^a-zA-Z0-9!?$£@:;'",.-\s]+/g, '')
        .substring(0, 100 - thisSuffix.length);

      setValue(FIELDS.PAGE_TITLE, `${thisNameFormatted}${thisSuffix}`);
    }
    // NB This page title will still be sent to the JG API, but it doesn't appear
    // to the user on the FSU form anymore.
  }, [firstName, orgName]);

  const renderJGContent = (isSchool && justGivingPageForSchools)
    || (isWork && justGivingPageForWorkplace)
    || (isPublic && justGivingPageForMyselfGroup)
    || showJGQuestion;

  // If the CMS has passed specific groups/segments to show the FR pack question to
  // check if the current segment is in that list:
  if (showFundraisingPackQuestion && mappedPackSegments) {
    showPackFiltered = mappedPackSegments.includes(selectedFundraisingSegment);
  }

  // Use a ref to track Fundraising Pack
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the reset on initial mount to preserve the default value from constants
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Reset to "NO" when segment changes
    setValue(FIELDS.FUNDRAISING_PACK, PRINTED_FUNDRAISING_PACK.NO.value);
  }, [selectedFundraisingSegment, setValue]);

  return (
    <SoftHider show={!isSubmitted} data-test="section-3">
      {/* Fundraising pack question shown/hide based on CMS settings */}
      <SoftHider show={showPackFiltered} data-test="fr-pack-fields">
        <FundraisingPackWrapper>
          <Fieldset
            legend={title}
          >
            {description && (
              <DescriptionWrapper>
                {renderBody(description)}
              </DescriptionWrapper>
            )}
            <RadioButtonWrapper>
              <RadioButton
                {...register(FIELDS.FUNDRAISING_PACK)}
                name={FIELDS.FUNDRAISING_PACK}
                value={PRINTED_FUNDRAISING_PACK.YES.value}
                label={PRINTED_FUNDRAISING_PACK.YES.displayValue}
                data-test="fundraising-pack-yes"
              />
              <RadioButton
                {...register(FIELDS.FUNDRAISING_PACK)}
                name={FIELDS.FUNDRAISING_PACK}
                value={PRINTED_FUNDRAISING_PACK.NO.value}
                label={PRINTED_FUNDRAISING_PACK.NO.displayValue}
                data-test="fundraising-pack-no"
              />
            </RadioButtonWrapper>
          </Fieldset>
        </FundraisingPackWrapper>
      </SoftHider>

      <SoftHider show={renderJGContent} data-test="justgiving-fields">
        <JustGivingWrapper>
          <JustGivingFieldset legend={justGivingLabel}>
            <JustGivingPill aria-hidden="true">
              <img src={JustGivingWhiteLogo} alt="JustGiving" />
            </JustGivingPill>
            {justGivingDescription && renderBody(justGivingDescription)}
          </JustGivingFieldset>
        </JustGivingWrapper>
      </SoftHider>

      <MarketingPreferencesWrapper>
        <MarketingPreferencesDS
          id="marketing_prefs"
          formContext={methods}
          mpValidationOptions={mpValidationOptions}
          copyTop={mpTopCopy(campaignCode)}
        />
      </MarketingPreferencesWrapper>
      <Divider />

      {/* <PrizeDrawOptIn register={register} /> */}
      <TermsAndConditions
        register={register}
        currentError={getError(FIELDS.OVER_EIGHTEEN)}
      />

      <SubmitContainer>
        <SubmitButton
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
          data-test="submit-fsu-form"
        >
          {isSubmitting ? 'Submitting...' : 'Register to fundraise'}
        </SubmitButton>
      </SubmitContainer>

    </SoftHider>
  );
};

JustGivingMarketingSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.shape({ raw: PropTypes.string.isRequired }),
  showFundraisingPackQuestion: PropTypes.bool.isRequired,
  fundraisingPackSegments: PropTypes.arrayOf(PropTypes.string),
  mpValidationOptions: MpValidationOptionsPropTypes.isRequired,
  justGivingLabel: PropTypes.string.isRequired,
  justGivingDescription: PropTypes.shape({ raw: PropTypes.string.isRequired }),
  campaignCode: PropTypes.string.isRequired,
  justGivingPageForSchools: PropTypes.bool.isRequired,
  justGivingPageForWorkplace: PropTypes.bool.isRequired,
  justGivingPageForMyselfGroup: PropTypes.bool.isRequired,
  showJGQuestion: PropTypes.bool.isRequired
};

export default JustGivingMarketingSection;
